import { useEffect, useRef, useState, type AnchorHTMLAttributes, type MouseEvent } from "react";
import { Link, useLocation } from "react-router-dom";

import { homePathForUser, useOptionalAuth, type AuthState } from "../../auth/AuthContext";
import Icon from "../../components/icons";
import { publicAsset } from "./publicAsset";
import "./SiteHeader.css";

/** Páginas públicas de torneos123.com (mismo valor que `page` de
 * Torneos123LandingPage). */
export type SitePage = "home" | "tournaments" | "organizer" | "player" | "privacy";

/** Secciones de /organizador a las que se llega desde el menú y el pie. */
export type OrganizerSection = "demo" | "precio";

/** Inicio público: en torneos123.com es la raíz; en cualquier otro host la
 * raíz manda al login, así que el inicio vive en /torneos123. */
export function siteHomePath(hostname: string = window.location.hostname): string {
  return hostname === "torneos123.com" || hostname === "www.torneos123.com" ? "/" : "/torneos123";
}

/** Dentro de /organizador basta el ancla (#demo); desde otra página se va a
 * /organizador#demo y useHashScroll baja a la sección al llegar. */
export function organizerSectionHref(page: SitePage, section: OrganizerSection): string {
  return page === "organizer" ? `#${section}` : `/organizador#${section}`;
}

type SiteLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & { to: string };

/** Enlace interno: ruta de la app con <Link> (respeta el basename /tenis) o
 * ancla de la misma página con <a href="#…"> (el navegador baja aunque el
 * hash ya sea el mismo). */
export function SiteLink({ to, ...rest }: SiteLinkProps) {
  if (to.startsWith("#")) return <a href={to} {...rest} />;
  return <Link to={to} {...rest} />;
}

interface NavItem {
  label: string;
  to: (page: SitePage) => string;
  current?: SitePage;
}

const NAV_ITEMS: NavItem[] = [
  { label: "Torneos", to: () => "/torneos", current: "tournaments" },
  { label: "Organizadores", to: () => "/organizador", current: "organizer" },
  { label: "Jugadores", to: () => "/jugador", current: "player" },
  { label: "Precio", to: (page) => organizerSectionHref(page, "precio") },
];

/** Botón de cuenta del encabezado: «Iniciar sesión» (al login) o, con la
 * sesión ya abierta, «Mi cuenta» directo a su panel (misma regla que el
 * login: homePathForUser). Mientras la sesión se verifica sigue «Iniciar
 * sesión»: /login reenvía solo si la sesión resulta válida. */
export function accountLink(auth: AuthState | undefined): { label: string; to: string; signedIn: boolean } {
  if (auth?.status === "authenticated" && auth.user) {
    return { label: "Mi cuenta", to: homePathForUser(auth.user), signedIn: true };
  }
  return { label: "Iniciar sesión", to: "/login", signedIn: false };
}

const MENU_PANEL_ID = "t123-menu-panel";
const DESKTOP_QUERY = "(min-width: 861px)";
const LOGO_WIDTH = 900;
const LOGO_HEIGHT = 187;

/** Encabezado de las páginas públicas. Escritorio: barra oscura con 4 enlaces,
 * «Iniciar sesión» y «Pedir demo». Celular (≤860): una sola fila de 60 px con
 * logo, «Iniciar sesión» y «Pedir demo» compactos y botón de menú (pedido de
 * Eduardo 2026-10-01: entrar no debe esconderse en el menú); el panel se
 * cierra con Escape, al tocar fuera, al tocar un enlace o al cambiar de ruta.
 * Con sesión abierta, «Iniciar sesión» se vuelve «Mi cuenta». */
export function SiteHeader({ page }: { page: SitePage }) {
  const [open, setOpen] = useState(false);
  const account = accountLink(useOptionalAuth());
  const location = useLocation();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (!open) return;
    const isInside = (target: EventTarget | null) =>
      target instanceof Node &&
      Boolean(panelRef.current?.contains(target) || toggleRef.current?.contains(target));

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      toggleRef.current?.focus();
    };
    const onPointerDown = (event: PointerEvent) => {
      if (!isInside(event.target)) setOpen(false);
    };
    // Con teclado: si el foco sale del panel (Tab al contenido), se cierra.
    const onFocusIn = (event: FocusEvent) => {
      if (!isInside(event.target)) setOpen(false);
    };
    const desktop = typeof window.matchMedia === "function" ? window.matchMedia(DESKTOP_QUERY) : null;
    const onViewportChange = () => {
      if (desktop?.matches) setOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("focusin", onFocusIn);
    desktop?.addEventListener?.("change", onViewportChange);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("focusin", onFocusIn);
      desktop?.removeEventListener?.("change", onViewportChange);
    };
  }, [open]);

  // Tocar cualquier enlace del encabezado cierra el panel (también si el
  // ancla ya era la actual y la ruta no cambia).
  const closeOnLink = (event: MouseEvent<HTMLElement>) => {
    if (event.target instanceof Element && event.target.closest("a")) setOpen(false);
  };

  const demoHref = organizerSectionHref(page, "demo");
  const homePath = siteHomePath();

  return (
    <header className={`t123-nav${open ? " is-menu-open" : ""}`} onClick={closeOnLink}>
      <div className="t123-shell t123-nav__inner">
        <Link className="t123-brand" to={homePath} aria-label="Torneos123, ir al inicio">
          <img
            src={publicAsset("torneos123/logo-torneos123-header-on-dark.png")}
            alt="Torneos123"
            width={LOGO_WIDTH}
            height={LOGO_HEIGHT}
            decoding="async"
          />
        </Link>

        <nav className="t123-nav__links" aria-label="Principal">
          {NAV_ITEMS.map((item) => (
            <SiteLink key={item.label} to={item.to(page)} aria-current={item.current === page ? "page" : undefined}>
              {item.label}
            </SiteLink>
          ))}
        </nav>

        <div className="t123-nav__actions">
          <Link className="t123-btn t123-btn--outline t123-nav__login" to={account.to}>
            {account.label}
          </Link>
          <SiteLink className="t123-btn t123-btn--primary t123-nav__demo" to={demoHref} data-t123-header-cta="">
            Pedir demo
          </SiteLink>
        </div>

        <button
          ref={toggleRef}
          type="button"
          className="t123-nav__toggle"
          aria-label={open ? "Cerrar menú" : "Abrir menú"}
          aria-expanded={open}
          aria-controls={MENU_PANEL_ID}
          onClick={() => setOpen((value) => !value)}
        >
          <Icon name={open ? "close" : "menu"} />
        </button>
      </div>

      <div ref={panelRef} id={MENU_PANEL_ID} className="t123-nav__panel" hidden={!open}>
        <nav aria-label="Menú">
          <ul>
            {NAV_ITEMS.map((item) => (
              <li key={item.label}>
                <SiteLink to={item.to(page)} aria-current={item.current === page ? "page" : undefined}>
                  {item.label}
                </SiteLink>
              </li>
            ))}
            <li className="t123-nav__panel-login">
              <Link to={account.to}>
                <Icon name={account.signedIn ? "user" : "log-in"} />
                {account.label}
              </Link>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
}
